import React, { useCallback, useId, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { IconXmark } from '../../icons';
import Chip from '../core/Chip';

interface CreatableMultiselectProps {
  label?: string;
  placeholder?: string;
  values: string[];
  staticValues?: string[];
  onChange: (values: string[]) => void;
}

const messages = defineMessages({
  remove: {
    id: 'creatable_multi_select.remove_value',
    defaultMessage: 'Remove {value}',
  },
});

const CreatableMultiSelect: React.FC<CreatableMultiselectProps> = ({
  label,
  placeholder,
  staticValues,
  values,
  onChange,
}) => {
  const intl = useIntl();
  const id = useId();
  const [inputValue, setInputValue] = useState('');

  const statics = useMemo(() => staticValues || [], [staticValues]);

  const commit = useCallback(
    (raw: string) => {
      const addition = raw.trim();
      setInputValue('');
      if (!addition || values.includes(addition)) {
        return;
      }
      const newValues = values.filter((v) => !statics.includes(v));
      onChange([...statics, ...newValues, addition]);
    },
    [onChange, statics, values],
  );

  const remove = useCallback(
    (value: string) => {
      if (statics.includes(value)) {
        return;
      }
      const newValues = values.filter(
        (v) => !statics.includes(v) && v !== value,
      );
      onChange([...statics, ...newValues]);
    },
    [onChange, statics, values],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit(inputValue);
        return;
      }
      if (event.key === 'Backspace' && inputValue === '') {
        const last = [...values].reverse().find((v) => !statics.includes(v));
        if (last) {
          event.preventDefault();
          remove(last);
        }
      }
    },
    [commit, inputValue, remove, statics, values],
  );

  return (
    <div className="flex w-full flex-col">
      {label && (
        <label
          htmlFor={id}
          className="text-xs leading-4 tracking-[0.5px] text-text-800"
        >
          {label}
        </label>
      )}
      <div className="flex w-full flex-wrap items-center border-b border-background-200 py-1 focus-within:border-background-400">
        {values.map((value) => (
          <Chip
            key={value}
            name={value}
            filled={false}
            endAdornment={
              statics.includes(value) ? undefined : (
                <button
                  type="button"
                  aria-label={intl.formatMessage(messages.remove, { value })}
                  className="-mr-1 ml-1 flex items-center"
                  onClick={() => remove(value)}
                >
                  <IconXmark className="size-4" />
                </button>
              )
            }
          />
        ))}
        <input
          id={id}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          className="min-w-20 grow border-0 bg-transparent p-0 text-base placeholder:text-text-700 focus:outline-none focus:ring-0"
          onChange={(event) => setInputValue(event.currentTarget.value)}
          onKeyDown={onKeyDown}
        />
      </div>
    </div>
  );
};

export default CreatableMultiSelect;
